import React, { createContext, useEffect, useState, useRef, useContext } from 'react'

import { OrderEngineClient, DeviceTypes, OrderStates } from '../orderengine-client/index'

import { OkeDeviceContext } from './OkeDeviceContext'
import { OkeFrameContext } from './OkeFrameContext'
import { OkeMonitorContext } from './OkeMonitorContext';
import { UUID } from "uuidjs";

import { brandParams } from '../api/brandParams'

import cloneDeep from 'lodash.clonedeep'

import { localStorage } from 'okeoke.localstorage';

const OrderEngineContext = createContext()

function transcodeID(value, keyName) {
  if (value == null) return {}
  const map = value.reduce(
    (obj, item) => ({
      ...obj,
      [item[keyName]]: { ...item }
    }),
    {}
  )
  return map
}

function OrderEngineProvider(props) {
  const { app: appType, token: appToken, children, baseUrl } = props
  const uid = useRef(UUID.generate())
  const { setEntity, entityState, entities } = useContext(OkeMonitorContext) || {}
  const [client, setClient] = useState(null)
  const [brandID, setBrandID] = useState(null)
  const [brandName, setBrandName] = useState(null)
  const [apiUrl, setApiUrl] = useState(null)
  const [token, setToken] = useState(null)
  const [tokenData, setTokenData] = useState(null)
  const [app, setApp] = useState(null)
  const [selectedLocationUUID, setSelectedLocationUUID] = useState(localStorage.getItem('lsl_uuid') || null)
  const [defaultLocationUUID, setDefaultLocationUUID] = useState(null)
  const [activeBasketUUID, setActiveBasketUUID] = useState(null)
  const [languageCode, setLanguageCode] = useState(null)
  const [defaultLanguageCode, setDefaultLanguageCode] = useState(null)
  const [catalogLoading, setCatalogLoading] = useState(true)
  const [socketConnected, setSocketConnected] = useState(false)
  const [socketAuthenticated, setSocketAuthenticated] = useState(false)

  const [appParams, setAppParams] = useState(null)

  const [catalogUpdated, setCatalogUpdated] = useState(false)

  const [locations, setLocations] = useState(null)
  const [languages, setLanguages] = useState({})
  const [currencies, setCurrencies] = useState({})

  const sandboxModeRef = useRef(false)

  const [ordersInited, setOrdersInited] = useState(false)

  const [orders, setOrders] = useState({})
  const ordersRef = useRef({})

  const [baskets, setBaskets] = useState({})
  const basketsRef = useRef({})

  const [contextInited, setContextInited] = useState(false)

  const [deviceStates, setDeviceStates] = useState({})
  const deviceStatesRef = useRef({})

  const { hasDeviceContext, loggedIn, brandID: deviceBrandID, deviceType, token: deviceToken, deviceUUID, apiUrl: deviceApiUrl,
    logoffDevice, loggedInUser, deviceParams, previewMode,
    deviceLocationUUID, deviceDemo, deviceSandbox, setDeviceParams, setDevicePaymentAgents, setSubDevices, setDeviceName,
    clientUID, setDisabled, devicePaymentAgents, setLocalUsers } = useContext(OkeDeviceContext) || {}

  const [sandboxMode, setSandboxModeState] = useState((deviceDemo || deviceSandbox) ? true : false)

  const { hasFrameContext, contextInited: frameContextInited, frameType, frameVersion, sendMessageToWebView, systemInfo } = useContext(OkeFrameContext) || {}

  const assetCache = useRef({})

  const onOrderUpdated = (e) => {
    if (e.order?.sandbox != sandboxModeRef.current) return
    if (e.order.orderState >= OrderStates.CLOSED_SUCCESS) {
      if (basketsRef.current[e.order.uuid]) {
        delete basketsRef.current[e.order.uuid]
        setBaskets(cloneDeep(basketsRef.current))
      }
      if (!ordersRef.current[e.order.uuid]) return
    } else {
      if (e.order.orderState !== OrderStates.BASKET) {
        if (basketsRef.current[e.order.uuid]) {
          delete basketsRef.current[e.order.uuid]
          setBaskets(cloneDeep(basketsRef.current))
        }
      }
    }
    if (e.order.orderState > OrderStates.BASKET) {
      ordersRef.current[e.order.uuid] = cloneDeep(e.order)
      ordersRef.current[e.order.uuid].assembledOrderItems = cloneDeep(e?.assembledOrderItems || {})
      setOrders(cloneDeep(ordersRef.current))
    }
    if (e.order.orderState === OrderStates.BASKET) {
      basketsRef.current[e.order.uuid] = cloneDeep(e.order)
      basketsRef.current[e.order.uuid].assembledOrderItems = cloneDeep(e?.assembledOrderItems || {})
      setBaskets(cloneDeep(basketsRef.current))
    }
  }

  useEffect(() => {
    if (hasDeviceContext == null) {
      brandParams.getBrandParams(baseUrl || window.location.origin, appType).then(res => {
        if (res.success) {
          setApiUrl(baseUrl || window.location.origin)
          setToken(appToken)
          setApp(appType)
          setBrandID(res.data.brandID)
          setBrandName(res.data.brandName)
          setAppParams(res.data.params)
        }
      })
    }
  }, [])

  useEffect(() => {
    if (appToken !== token) {
      setToken(appToken)
    }
  }, [appToken])

  useEffect(() => {
    deviceStatesRef.current = deviceStates
  }, [deviceStates])

  useEffect(() => {
    if (client != null) client.setToken(token)
  }, [client, token])

  useEffect(() => {
    //console.log(deviceParams)
    if (deviceDemo || deviceSandbox) {
      setSandboxModeState(true)
    }
  }, [deviceDemo, deviceSandbox])

  useEffect(() => {
    if (deviceParams?.integrations?.fruitsys?.sendDirect) {
      if (locations?.[deviceLocationUUID]?.localConfig?.fruit?.useExternalOrderManager) {
        if (deviceParams?.integrations?.fruitsys?.sendDirect) {
          setEntity({ id: 'fruitsys.server', host: locations?.[deviceLocationUUID]?.localConfig?.fruit?.serverIP })
        }
      }
    }
  }, [deviceParams, locations])

  useEffect(() => {
    if (deviceType != null && devicePaymentAgents != null) {
      for (let agent of devicePaymentAgents || []) {
        if (['saltpayTerminal', 'vivaTerminal'].includes(agent?.useTerminal?.terminalType)) {
          setEntity({ id: `payment.${agent?.useTerminal?.terminalType}.${agent?.paymentAgentID || 0}`, host: agent?.useTerminal?.params?.ipAddress })
        }
      }
    }
  }, [devicePaymentAgents, deviceType])

  useEffect(() => {
    if (hasDeviceContext && deviceUUID != null) {
      if (loggedIn && deviceBrandID && deviceApiUrl && deviceType) {
        brandParams.getBrandParams(deviceApiUrl, DeviceTypes[deviceType]).then(res => {
          if (res.success) {
            setBrandName(res.data.brandName)
            setAppParams(res.data.params)
          }
        })
        if (client != null) {
          if (token !== deviceToken) {
            setToken(deviceToken)
          }
        } else {
          setApp(DeviceTypes[deviceType])
          setBrandID(deviceBrandID)
          setApiUrl(deviceApiUrl)
          setToken(deviceToken)
        }
      }
    }
  }, [hasDeviceContext, loggedIn, deviceBrandID, deviceApiUrl, deviceToken, deviceType, deviceUUID])

  const refreshUsers = async () => {
    if (hasDeviceContext) {
      let res = await client.getLocalUsers()
      if (res.success) {
        setLocalUsers(res.data)
      }
    }
  }

  useEffect(() => {
    if (hasDeviceContext && socketAuthenticated) {
      refreshUsers()
    }
  }, [hasDeviceContext, socketAuthenticated])

  useEffect(() => {
    if (apiUrl && app && brandID && ((deviceParams && clientUID) || !hasDeviceContext)) {
      if (client == null) {
        if ((hasFrameContext && frameContextInited) || (!hasFrameContext)) {
          let activeClient = new OrderEngineClient(
            apiUrl,
            app,
            token,
            { frameType, frameVersion, ...systemInfo },
            deviceParams,
            clientUID,
            sendMessageToWebView
          )
          activeClient.init()
          setClient(activeClient)
          setContextInited(true)
        }
      } else {
        client.setToken(token)
      }
    }
  }, [apiUrl, app, token, brandID, hasFrameContext, frameContextInited, frameType, frameVersion, clientUID, deviceParams])

  useEffect(() => {
    for (let lang of Object.values(languages)) {
      if (lang.isDefault) {
        if (languageCode == null) setLanguageCode(lang.code)
        setDefaultLanguageCode(lang.code)
      }
    }
  }, [languages])

  useEffect(() => {
    if (catalogLoading === false) {
      setLanguages(client.catalog.languages)
      setCurrencies(client.catalog.currencies)
    }
  }, [catalogLoading])

  useEffect(() => {
    if (selectedLocationUUID != null) {
      localStorage.setItem('lsl_uuid', selectedLocationUUID)
    }
  }, [selectedLocationUUID])

  useEffect(() => {
    if (deviceLocationUUID != null && selectedLocationUUID !== deviceLocationUUID) {
      setSelectedLocationUUID(deviceLocationUUID)
    } else {
      if (selectedLocationUUID == null && localStorage.getItem('lsl_uuid') == null && defaultLocationUUID != null) { // set default location uuid
        setSelectedLocationUUID(defaultLocationUUID)
      }
    }
  }, [locations, deviceLocationUUID, defaultLocationUUID])

  const getDevicesStates = () => {
    return new Promise((resolve, reject) => {
      let finished = false
      setTimeout(() => {
        if (!finished) {
          finished = true
          resolve({ success: false, message: 'Timeout!', code: 408, data: { errMsgCode: 'request.error.timeout' } })
        }
      }, 5000)
      client?.clientSocket.ioclient.emit('getDevicesStates', {}, (data) => {
        if (!finished) {
          finished = true
          if (data.success) setDeviceStates(transcodeID(data.data, 'uuid'))
          resolve(data)
        }
      })
    })
  }

  const onConnectionChange = async (data) => {
    switch (data.state) {
      case 'Connected': setSocketConnected(true)
        break
      case 'Disconnected': setSocketConnected(false)
        break
      case 'Authorized': setSocketAuthenticated(true)
        if (loggedInUser != null) {
          client.setDeviceUser(loggedInUser.userUUID, loggedInUser.pinCode)
        }
        // Bind the client to the device locationUUID
        setTokenData(data.data.data)
        getDevicesStates()
        break
      case 'Unauthorized': setSocketAuthenticated(false)
        break
      default:
    }
  }

  const onCatalogStateChange = (data) => {
    setCatalogLoading(data.state !== 'Loaded')
  }

  const onLocationUpdated = (e) => {
    if (client.locationsModel?.locations != null) setLocations(transcodeID(client.locationsModel.locations, 'uuid'))
  }

  const onLocationsLoaded = (data) => {
    if (data.locations != null) {
      setLocations(transcodeID(data.locations, 'uuid'))
      setDefaultLocationUUID(client.locationsModel.locationUUID)
      if (selectedLocationUUID == null && localStorage.getItem('lsl_uuid') == null) {
        setSelectedLocationUUID(client.locationsModel.locationUUID)
      }
    }
  }

  const onOrdersInited = (data) => {
    setOrdersInited(data.inited)
    if (data.inited && data.activeBasketUUID != null) setActiveBasketUUID(data.activeBasketUUID)
  }

  const onDeviceForceLogout = () => {
    if (hasDeviceContext) logoffDevice()
  }

  const onDeviceDisableDuplicate = () => {
    client?.disable()
    setDisabled(true)
  }

  const onCatalogUpdated = () => {
    console.log('Catalog Updated')
    setCatalogUpdated(true)
  }

  const onAppParamsUpdated = (data) => {
    if (data.app === app) setAppParams(data.params.combined)
    else {
      if (data.app === 'base') {
        brandParams.getBrandParams(apiUrl, app).then(res => {
          if (res.success) {
            setAppParams(res.data.params)
          }
        })
      }
    }
  }

  const onDeviceParamsUpdated = (data) => {
    if (data?.uuid === deviceUUID && data.deviceParams != null) {
      setDeviceParams({ ...data.deviceParams })
      if (data.deviceName) {
        setDeviceName(data.deviceName)
      }
      setDevicePaymentAgents([...data.paymentAgents])
      setSubDevices([...data.subDevices])
    }
  }

  const onDeviceStateUpdated = (data) => {
    setDeviceStates({ ...deviceStatesRef.current, [data.uuid]: { ...data.deviceState } })
  }

  useEffect(() => {
    if (client != null) {
      setSocketConnected(client.clientSocket.connected)
      setSocketAuthenticated(client.clientSocket.authorized)
      setCatalogLoading(client.catalogLoading == null ? true : client.catalogLoading)
      setOrdersInited(client.orders?.inited || false)
      setLocations(transcodeID(client.locationsModel.locations, 'uuid'))
      setLanguages(client.catalog?.languages || null)
      setCurrencies(client.catalog?.currencies || null)
      setTokenData(client.tokenData)

      let activeOrders = {}
      let baskets = {}
      for (let activeOrder of Object.values(client?.orders?.orders || {})) {
        if (activeOrder?.order?.sandbox != sandboxModeRef.current) continue
        if (activeOrder?.order?.orderState > OrderStates.BASKET && activeOrder?.order?.orderState < OrderStates.CLOSED_SUCCESS) {
          activeOrders[activeOrder.order.uuid] = { ...activeOrder.order }
          activeOrders[activeOrder.order.uuid].assembledOrderItems = cloneDeep(activeOrder?.assembledOrderItems || {})
        }
        if (activeOrder?.order?.orderState === OrderStates.BASKET) {
          baskets[activeOrder.order.uuid] = { ...activeOrder.order }
          baskets[activeOrder.order.uuid].assembledOrderItems = cloneDeep(activeOrder?.assembledOrderItems || {})
        }
      }
      setOrders(activeOrders)
      setBaskets(baskets)
      ordersRef.current = activeOrders

      client.orders.addCallback('onOrderUpdated', uid.current, onOrderUpdated)
      client.addCallback('onOrdersInited', uid.current, onOrdersInited)
      client.addCallback('onConnectionChange', uid.current, onConnectionChange)
      client.addCallback('onCatalogStateChange', uid.current, onCatalogStateChange)

      client.locationsModel.addCallback('onLocationOpenStateUpdated', uid.current, onLocationUpdated)
      client.locationsModel.addCallback('onLocationExtendedPrepTimeUpdated', uid.current, onLocationUpdated)
      client.locationsModel.addCallback('onLocationCloudDeviceUpdated', uid.current, onLocationUpdated)

      client.addCallback('onLocationsLoaded', uid.current, onLocationsLoaded)
      client.clientSocket.addCallback('onDeviceForceLogout', uid.current, onDeviceForceLogout)
      client.clientSocket.addCallback('onDeviceDisableDuplicate', uid.current, onDeviceDisableDuplicate)
      client.clientSocket.addCallback('onCatalogUpdated', uid.current, onCatalogUpdated)
      client.clientSocket.addCallback('onAppParamsUpdated', uid.current, onAppParamsUpdated)
      client.clientSocket.addCallback('deviceParamsUpdated', uid.current, onDeviceParamsUpdated)
      client.clientSocket.addCallback('onDeviceStateUpdated', uid.current, onDeviceStateUpdated)
    }
    return () => {
      // ON UNMOUNT
      if (client != null) {
        client.orders.removeCallback('onOrderUpdated', uid.current)
        client.removeCallback('onOrdersInited', uid.current)
        client.removeCallback('onConnectionChange', uid.current)
        client.removeCallback('onCatalogStateChange', uid.current)
        client.locationsModel.removeCallback('onLocationOpenStateUpdated', uid.current)
        client.locationsModel.removeCallback('onLocationExtendedPrepTimeUpdated', uid.current)
        client.locationsModel.removeCallback('onLocationCloudDeviceUpdated', uid.current)
        client.removeCallback('onLocationsLoaded', uid.current)
        client.clientSocket.removeCallback('onDeviceForceLogout', uid.current)
        client.clientSocket.removeCallback('onDeviceDisableDuplicate', uid.current)
        client.clientSocket.removeCallback('onCatalogUpdated', uid.current)
        client.clientSocket.removeCallback('onAppParamsUpdated', uid.current)
        client.clientSocket.removeCallback('deviceParamsUpdated', uid.current)
        client.clientSocket.removeCallback('onDeviceStateUpdated', uid.current)
      }
    }
  }, [client])

  const loadImage = (url, id, cache, cbOnLoad, cbOnError) => {
    let image = new Image()
    image.src = url
    image.onerror = cbOnError
    image.onload = () => {
      if (cache) setCachedAsset(id, image)
      cbOnLoad()
    }
  }

  const getCachedAsset = (id) => {
    return assetCache.current[id] || null
  }

  const setCachedAsset = (id, img) => {
    assetCache.current[id] = img
  }

  const setSandboxMode = (mode) => {
    if (deviceSandbox || deviceDemo) {
      return false
    } else {
      setSandboxModeState(mode ? true : false)
    }
  }

  const addOrderStatus = async (orderUUID, statusID, notes) => {
    let orderObj = client?.orders?.orders[orderUUID] || null
    if (orderObj != null) {
      let res = await orderObj.statuses.addStatus(statusID, notes)
      return res
    } else {
      return { success: false, message: 'Order not open in client' }
    }
  }

  const addOrderItemStatus = async (orderUUID, orderItemUUID, statusID, notes) => {
    let orderObj = client?.orders?.orders[orderUUID] || null
    if (orderObj != null) {
      let res = await orderObj.statuses.addItemStatus(orderItemUUID, statusID, notes)
      //console.log('addItemStatus: ', res)
      return res
    } else {
      return { success: false, message: 'Order not open in client' }
    }
  }

  const setOrderTable = async (orderUUID, tableUUID) => {
    let orderObj = client?.orders?.orders[orderUUID] || null
    if (orderObj != null) {
      let res = await orderObj.general.setTable(tableUUID)
      return res
    } else {
      return { success: false, message: 'Order not open in client' }
    }
  }

  const removeClosedOrder = (orderUUID) => {
    delete ordersRef.current[orderUUID]
    setOrders(cloneDeep(ordersRef.current))
  }

  useEffect(() => {
    if (sandboxModeRef.current !== sandboxMode) {
      sandboxModeRef.current = sandboxMode
      if (client != null) {
        let activeOrders = {}
        let baskets = {}
        for (let activeOrder of Object.values(client?.orders?.orders || {})) {
          if (activeOrder?.order?.sandbox != sandboxModeRef.current) continue
          if (activeOrder?.order?.orderState > OrderStates.BASKET && activeOrder?.order?.orderState < OrderStates.CLOSED_SUCCESS) {
            activeOrders[activeOrder.order.uuid] = { ...activeOrder.order }
            activeOrders[activeOrder.order.uuid].assembledOrderItems = cloneDeep(activeOrder?.assembledOrderItems || {})
          }
          if (activeOrder?.order?.orderState === OrderStates.BASKET) {
            baskets[activeOrder.order.uuid] = { ...activeOrder.order }
            baskets[activeOrder.order.uuid].assembledOrderItems = cloneDeep(activeOrder?.assembledOrderItems || {})
          }
        }
        setOrders(activeOrders)
        setBaskets(baskets)
        ordersRef.current = activeOrders
      }
    }
  }, [sandboxMode, client])

  return (
    <OrderEngineContext.Provider
      value={{
        iExist: true,
        client,
        app,
        brandID,
        brandName,
        apiUrl,
        token,
        tokenData,
        languageCode,
        catalogLoading,
        catalogUpdated,
        setCatalogUpdated,
        contextInited,
        socketAuthenticated,
        socketConnected,
        locations,
        languages,
        currencies,
        ordersInited,
        defaultLanguageCode,
        activeBasketUUID,
        selectedLocationUUID,
        defaultLocationUUID,
        deviceLocationUUID,
        setActiveBasketUUID,
        setLanguageCode,
        setSelectedLocationUUID,
        appParams,
        getCachedAsset,
        setCachedAsset,
        loadImage,
        deviceParams,
        devicePaymentAgents,
        previewMode,
        sandboxMode,
        setSandboxMode,
        demoMode: deviceDemo,
        orders,
        baskets,
        addOrderStatus,
        addOrderItemStatus,
        setOrderTable,
        removeClosedOrder,
        entityState,
        entities,
        deviceStates,
        deviceSandbox,
        setToken,
        refreshUsers
      }}
    >
      {children}
    </OrderEngineContext.Provider>
  )
}

export { OrderEngineContext, OrderEngineProvider }
